import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getSearchSessionResults,
  getSearchSessions,
  submitCustomSearchMatchFeedback,
  fetchSearchClarifications,
  fetchSearchEnhancement,
} from '../services/api';
import { useCustomSearch } from '../contexts/CustomSearchContext';
import CandidateProfileModal from './CandidateProfileModal';
import './SearchCandidates.css';
import './Matchmaker.css';

function formatMatchDateLine(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const dateStr = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const timeStr = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `Matched ${dateStr} at ${timeStr}`;
}

// All 17 countries supported by the Pearch integration
const ALL_PEARCH_COUNTRIES = [
  'Argentina', 'Belize', 'Brazil', 'Colombia', 'Costa Rica',
  'Dominican Republic', 'Ecuador', 'El Salvador', 'Guatemala', 'Honduras',
  'Mexico', 'Nicaragua', 'Panama', 'Peru', 'Philippines', 'Venezuela', 'Portugal',
];

const INDUSTRY_OPTIONS = [
  'SaaS / Software', 'Finance', 'Healthcare', 'FMCG / Retail', 'Energy', 'Consulting',
];

// Mutually-exclusive contact filter options
const FILTER_OPTIONS = [
  { id: '', label: 'No requirement' },
  { id: 'filter_out_no_emails', label: 'Must have email' },
  { id: 'filter_out_no_phones', label: 'Must have phone' },
  { id: 'filter_out_no_phones_or_emails', label: 'Either email or phone' },
];

const DEFAULT_SF = {
  query: '',
  titles: '',
  locations: [],        // selected from ALL_PEARCH_COUNTRIES
  excludedCountries: [], // selected from ALL_PEARCH_COUNTRIES
  industries: [],
  customIndustry: '',   // value when 'Other' industry is selected
  strictFilters: false,
  searchType: 'pro',
  limit: 10,
  insights: true,
  highFreshness: false,
  filterOption: '',      // single-select: '', filter_out_no_emails, filter_out_no_phones, filter_out_no_phones_or_emails
  revealOptions: [],     // multi-select: reveal_emails, reveal_phones
};

const SearchIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"
      fill="currentColor"
    />
  </svg>
);

function InfoTooltip({ text }) {
  return (
    <span className="sc-info-tooltip" tabIndex={0} aria-label={text}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
          fill="#94a3b8"
        />
      </svg>
      <span className="sc-tooltip" role="tooltip">{text}</span>
    </span>
  );
}

const RECENT_PAGE_SIZE = 5;

function SearchCandidates() {
  const navigate = useNavigate();
  const { startBackgroundSearch, isSearching } = useCustomSearch();

  const [searchMode, setSearchMode] = useState('simple');
  const [query, setQuery] = useState('');
  const [sf, setSf] = useState(DEFAULT_SF);
  const [searchError, setSearchError] = useState(null);
  const [results, setResults] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [starting, setStarting] = useState(false);

  const [selectedMatch, setSelectedMatch] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [thumbsDownModal, setThumbsDownModal] = useState({ open: false, matchId: null });
  const [thumbsDownReason, setThumbsDownReason] = useState('');

  const [lastSearchPayload, setLastSearchPayload] = useState(null); // { query, searchParams }

  // Clarification flow state machine
  // null | 'clarifying' | { questions } | 'enhancing' | { enhanced_query }
  const [clarifyState, setClarifyState] = useState(null);
  const [clarifyAnswers, setClarifyAnswers] = useState({});
  const [pendingQuery, setPendingQuery] = useState('');
  const [pendingSearchParams, setPendingSearchParams] = useState(null);

  const [recentSessions, setRecentSessions] = useState([]);
  const [recentLoading, setRecentLoading] = useState(true);
  const [recentPage, setRecentPage] = useState(1);

  const normalizeCustomSearchMatch = (m) => ({
    ...m,
    feedback: m.feedback === 'none' ? null : m.feedback,
  });
  const setResultsNormalized = (list) => setResults((list || []).map(normalizeCustomSearchMatch));

  const loadRecentSessions = useCallback(async () => {
    setRecentLoading(true);
    setRecentPage(1);
    try {
      const data = await getSearchSessions(50);
      setRecentSessions(data.sessions || []);
    } catch {
      setRecentSessions([]);
    } finally {
      setRecentLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRecentSessions();
  }, [loadRecentSessions]);

  useEffect(() => {
    const onComplete = async (e) => {
      const sid = e.detail?.sessionId;
      if (!sid) return;
      try {
        const data = await getSearchSessionResults(sid);
        setResultsNormalized(data.matches);
        setSessionId(sid);
        setSearchError(null);
        loadRecentSessions();
      } catch (err) {
        setSearchError(
          err?.response?.data?.error || err?.message || 'Could not load search results.'
        );
      }
    };
    window.addEventListener('customSearchComplete', onComplete);
    return () => window.removeEventListener('customSearchComplete', onComplete);
  }, [loadRecentSessions]);

  const updateSf = useCallback((field, value) => {
    setSf((prev) => ({ ...prev, [field]: value }));
  }, []);

  const toggleSfChip = useCallback((field, value) => {
    setSf((prev) => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter((x) => x !== value)
        : [...prev[field], value],
    }));
  }, []);

  // Per-candidate credit breakdown (verify at apidocs.pearch.ai)
  const perCandidateCost =
    (sf.searchType === 'pro' ? 5 : 1) +
    (sf.insights ? 1 : 0) +
    (sf.highFreshness ? 2 : 0) +
    (sf.revealOptions.includes('reveal_emails') ? 6 : 0) +
    (sf.revealOptions.includes('reveal_phones') ? 6 : 0);

  const estimatedCredits = sf.limit * perCandidateCost;

  // ── Helpers: assemble query text and search params from current form state ──

  const assembleQuery = useCallback(() => {
    if (searchMode === 'simple') return query.trim();
    let full = sf.query.trim();
    if (sf.titles.trim()) full += ` Job titles include: ${sf.titles}.`;
    if (sf.locations.length) full += ` Locations: ${sf.locations.join(', ')}.`;
    if (sf.excludedCountries.length)
      full += ` Exclude candidates from: ${sf.excludedCountries.join(', ')}.`;
    const allIndustries = [
      ...sf.industries.filter((i) => i !== 'Other'),
      ...(sf.industries.includes('Other') && sf.customIndustry.trim()
        ? [sf.customIndustry.trim()]
        : []),
    ];
    if (allIndustries.length) full += ` Industries: ${allIndustries.join(', ')}.`;
    return full;
  }, [searchMode, query, sf]);

  const buildSearchParams = useCallback(() => {
    const allIndustries = [
      ...sf.industries.filter((i) => i !== 'Other'),
      ...(sf.industries.includes('Other') && sf.customIndustry.trim()
        ? [sf.customIndustry.trim()]
        : []),
    ];
    const customFilters = {};
    if (sf.locations.length) customFilters.locations = sf.locations;
    if (sf.titles.trim())
      customFilters.titles = sf.titles.split(',').map((t) => t.trim()).filter(Boolean);
    if (allIndustries.length) customFilters.industries = allIndustries;

    return {
      search_type: sf.searchType,
      limit: sf.limit,
      insights: sf.insights,
      strict_filters: sf.strictFilters,
      high_freshness: sf.highFreshness,
      filter_out_no_emails: sf.filterOption === 'filter_out_no_emails',
      filter_out_no_phones: sf.filterOption === 'filter_out_no_phones',
      filter_out_no_phones_or_emails: sf.filterOption === 'filter_out_no_phones_or_emails',
      reveal_emails: sf.revealOptions.includes('reveal_emails'),
      reveal_phones: sf.revealOptions.includes('reveal_phones'),
      custom_filters: Object.keys(customFilters).length > 0 ? customFilters : null,
    };
  }, [sf]);

  // ── Core dispatcher: fires the background search ──

  const fireSearch = useCallback(async (q, params) => {
    setClarifyState(null);
    setClarifyAnswers({});
    setStarting(true);
    setSearchError(null);
    setResults(null);
    setSessionId(null);
    setLastSearchPayload({ query: q, searchParams: params });
    try {
      await startBackgroundSearch(q, params);
    } catch (err) {
      setSearchError(err?.response?.data?.error || err?.message || 'Could not start search.');
    } finally {
      setStarting(false);
    }
  }, [startBackgroundSearch]);

  // ── Phase 1: intercept Search button → fetch clarification questions ──

  const handleSearch = useCallback(
    async (e) => {
      e?.preventDefault();
      if (isSearching || clarifyState) return;

      const assembled = assembleQuery();
      if (!assembled) {
        setSearchError(
          searchMode === 'simple'
            ? 'Please enter a search query.'
            : 'Please describe the ideal candidate.'
        );
        return;
      }

      const params = searchMode === 'structured' ? buildSearchParams() : null;
      setPendingQuery(assembled);
      setPendingSearchParams(params);
      setClarifyAnswers({});
      setClarifyState('clarifying');

      try {
        const data = await fetchSearchClarifications(assembled);
        if (data.questions?.length > 0) {
          setClarifyState(data);
        } else {
          await fireSearch(assembled, params);
        }
      } catch {
        await fireSearch(assembled, params);
      }
    },
    [isSearching, clarifyState, searchMode, assembleQuery, buildSearchParams, fireSearch]
  );

  // ── Phase 2: recruiter answers questions → fetch enhanced query ──

  const handleConfirmAnswers = useCallback(async () => {
    const answers = Object.values(clarifyAnswers);
    if (!answers.length) {
      await fireSearch(pendingQuery, pendingSearchParams);
      return;
    }
    setClarifyState('enhancing');
    try {
      const data = await fetchSearchEnhancement(pendingQuery, answers);
      const enhanced = data.enhanced_query || pendingQuery;
      setClarifyState({ enhanced_query: enhanced });
    } catch {
      await fireSearch(pendingQuery, pendingSearchParams);
    }
  }, [clarifyAnswers, pendingQuery, pendingSearchParams, fireSearch]);

  // ── Phase 3: recruiter approves preview → fire the search ──

  const handleRunSearch = useCallback(async () => {
    const queryToSend = clarifyState?.enhanced_query ?? pendingQuery;
    await fireSearch(queryToSend, pendingSearchParams);
  }, [clarifyState, pendingQuery, pendingSearchParams, fireSearch]);

  // ── Skip: bypass clarification at any stage ──

  const handleSkipClarify = useCallback(async () => {
    await fireSearch(pendingQuery, pendingSearchParams);
  }, [pendingQuery, pendingSearchParams, fireSearch]);

  const handleNewBatch = useCallback(async () => {
    if (!lastSearchPayload || isSearching) return;
    setStarting(true);
    setSearchError(null);
    setResults(null);
    setSessionId(null);
    try {
      await startBackgroundSearch(
        lastSearchPayload.query,
        lastSearchPayload.searchParams,
        true, // excludePrevious — re-uses the already-enhanced query silently
      );
    } catch (err) {
      setSearchError(err?.response?.data?.error || err?.message || 'Could not start search.');
    } finally {
      setStarting(false);
    }
  }, [lastSearchPayload, isSearching, startBackgroundSearch]);

  const handleRepeatSearch = useCallback(async (sessionQuery) => {
    if (isSearching) return;
    const q = sessionQuery.trim();
    setSearchMode('simple');
    setQuery(q);
    setStarting(true);
    setSearchError(null);
    setResults(null);
    setSessionId(null);
    setLastSearchPayload({ query: q, searchParams: null });
    try {
      await startBackgroundSearch(q);
    } catch (err) {
      setSearchError(err?.response?.data?.error || err?.message || 'Could not start search.');
    } finally {
      setStarting(false);
    }
  }, [isSearching, startBackgroundSearch]);

  const recentTotalPages = Math.max(1, Math.ceil(recentSessions.length / RECENT_PAGE_SIZE));
  const pagedSessions = recentSessions.slice(
    (recentPage - 1) * RECENT_PAGE_SIZE,
    recentPage * RECENT_PAGE_SIZE,
  );

  // Structured mode only requires the query field
  const isSubmitDisabled =
    starting ||
    isSearching ||
    !!clarifyState ||
    (searchMode === 'simple' ? !query.trim() : !sf.query.trim());

  const handleViewDetails = (match) => {
    setSelectedMatch(match);
    setShowModal(true);
  };

  const getMatchPercentageClass = (pct) => {
    if (pct >= 90) return 'high';
    if (pct >= 80) return 'medium';
    return 'low';
  };

  const updateResultFeedback = (matchId, feedback) => {
    setResults((prev) => {
      if (prev == null) return prev;
      return prev.map((m) => (m.id === matchId ? { ...m, feedback } : m));
    });
  };

  const handleCustomFeedback = async (matchId, feedbackType, notes = '') => {
    try {
      await submitCustomSearchMatchFeedback(matchId, feedbackType, notes);
      updateResultFeedback(matchId, feedbackType);
    } catch (err) {
      console.error('Error submitting custom search feedback:', err);
    }
  };

  const handleCustomReset = async (matchId) => {
    try {
      await submitCustomSearchMatchFeedback(matchId, null, '');
      updateResultFeedback(matchId, null);
    } catch (err) {
      console.error('Error resetting custom search feedback:', err);
    }
  };

  const openThumbsDown = (matchId) => {
    setThumbsDownReason('');
    setThumbsDownModal({ open: true, matchId });
  };

  const handleThumbsDownConfirm = async () => {
    if (thumbsDownModal.matchId == null) return;
    await handleCustomFeedback(thumbsDownModal.matchId, 'thumbs_down', thumbsDownReason);
    setThumbsDownModal({ open: false, matchId: null });
    setThumbsDownReason('');
  };

  const handleThumbsDownSkip = async () => {
    if (thumbsDownModal.matchId == null) return;
    await handleCustomFeedback(thumbsDownModal.matchId, 'thumbs_down', '');
    setThumbsDownModal({ open: false, matchId: null });
    setThumbsDownReason('');
  };

  const trimQuery = (q, max = 72) => {
    const t = (q || '').trim();
    if (t.length <= max) return t;
    return `${t.slice(0, max - 1)}…`;
  };

  const formatSearchDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="search-candidates-container">
      <div className="sc-header">
        <div className="sc-header-icon">
          <SearchIcon size={28} />
        </div>
        <div className="sc-header-text">
          <h1>Search Candidates</h1>
          <p>
            Run a natural language query directly. The search runs in the background — you can switch
            pages while it runs. Results are saved under{' '}
            <button className="sc-matchmaker-link" onClick={() => navigate('/matchmaker?tab=custom')}>
              Matchmaker → Custom Searches
            </button>
            .
          </p>
        </div>
      </div>

      <div className="sc-layout">
        <div className="sc-col-main">
          <form className={`sc-form${clarifyState ? ' sc-form--clarifying' : ''}`} onSubmit={handleSearch}>
            {/* ── Mode toggle ── */}
            <div className="sc-mode-toggle">
              <button
                type="button"
                className={`sc-mode-btn ${searchMode === 'simple' ? 'sc-mode-btn--active' : ''}`}
                onClick={() => setSearchMode('simple')}
              >
                Simple search
              </button>
              <button
                type="button"
                className={`sc-mode-btn ${searchMode === 'structured' ? 'sc-mode-btn--active' : ''}`}
                onClick={() => setSearchMode('structured')}
              >
                Structured search
              </button>
            </div>

            {searchMode === 'simple' ? (
              <>
                <label className="sc-label" htmlFor="sc-query">
                  Search query
                </label>
                <div className="sc-input-row">
                  <textarea
                    id="sc-query"
                    className="sc-textarea"
                    rows={3}
                    placeholder="e.g. Senior corporate attorney with 5+ years M&A experience, based in Latin America, fluent in Spanish and English"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    disabled={starting || isSearching || !!clarifyState}
                  />
                  <button className="sc-submit-btn" type="submit" disabled={isSubmitDisabled}>
                    {starting || isSearching ? (
                      <>
                        <span className="sc-spinner" />
                        Searching...
                      </>
                    ) : (
                      <>
                        <SearchIcon />
                        Search
                      </>
                    )}
                  </button>
                </div>
                <p className="sc-hint">
                  Uses the same Pearch parameters as the Romeo / Matchmaker flow. Searches can take
                  up to ~90 seconds; a confirmation will appear when finished.
                </p>
              </>
            ) : (
              /* ══════════════ STRUCTURED FORM ══════════════ */
              <>
                <div className="sc-structured-form">

                  {/* Section 1 — Role & query */}
                  <div className="sc-section">
                    <div className="sc-section-label">Role &amp; candidate description</div>

                    <label className="sc-label" htmlFor="sc-sq-query">
                      Describe the ideal candidate
                      <span className="sc-required"> *</span>
                    </label>
                    {/* Full-width textarea — no submit button alongside */}
                    <textarea
                      id="sc-sq-query"
                      className="sc-textarea sc-textarea--full"
                      rows={4}
                      placeholder="e.g. VP of Sales with B2B SaaS experience in LatAm, fluent in English and Spanish, 10+ years experience, currently employed at a company with 50–500 employees…"
                      value={sf.query}
                      onChange={(e) => updateSf('query', e.target.value)}
                      disabled={starting || isSearching}
                    />

                    <label className="sc-label sc-label--mt" htmlFor="sc-sq-titles">
                      Which titles or roles must match?{' '}
                      <span className="sc-optional">(optional)</span>
                    </label>
                    <input
                      id="sc-sq-titles"
                      className="sc-text-input"
                      type="text"
                      placeholder="e.g. VP of Sales, Head of Growth, Commercial Director — separate with commas"
                      value={sf.titles}
                      onChange={(e) => updateSf('titles', e.target.value)}
                      disabled={starting || isSearching}
                    />
                  </div>

                  {/* Section 2 — Location */}
                  <div className="sc-section">
                    <div className="sc-section-label">Location &amp; exclusions</div>

                    <div className="sc-field-row">
                      <span className="sc-field-desc">
                        Which locations should be included?{' '}
                        <span className="sc-optional">(optional)</span>
                        <InfoTooltip text="If no location is selected, all 17 supported countries are included by default." />
                      </span>
                      <div className="sc-chip-group sc-chip-group--countries">
                        {ALL_PEARCH_COUNTRIES.map((loc) => (
                          <button
                            key={loc}
                            type="button"
                            className={`sc-chip ${sf.locations.includes(loc) ? 'sc-chip--active' : ''}`}
                            onClick={() => toggleSfChip('locations', loc)}
                            disabled={starting || isSearching}
                          >
                            {loc}
                          </button>
                        ))}
                      </div>
                      {sf.locations.length > 0 && (
                        <button
                          type="button"
                          className="sc-clear-link"
                          onClick={() => updateSf('locations', [])}
                          disabled={starting || isSearching}
                        >
                          Clear selection
                        </button>
                      )}
                    </div>

                    <div className="sc-field-row">
                      <span className="sc-field-desc">
                        Which countries should be excluded?{' '}
                        <span className="sc-optional">(optional)</span>
                      </span>
                      <div className="sc-chip-group sc-chip-group--countries">
                        {ALL_PEARCH_COUNTRIES.map((c) => (
                          <button
                            key={c}
                            type="button"
                            className={`sc-chip sc-chip--exclusion ${sf.excludedCountries.includes(c) ? 'sc-chip--exclusion-active' : ''}`}
                            onClick={() => toggleSfChip('excludedCountries', c)}
                            disabled={starting || isSearching}
                            title={
                              sf.excludedCountries.includes(c)
                                ? `Remove ${c} from exclusions`
                                : `Exclude ${c}`
                            }
                          >
                            {sf.excludedCountries.includes(c) && (
                              <span className="sc-chip-minus" aria-hidden>−</span>
                            )}
                            {c}
                          </button>
                        ))}
                      </div>
                      {sf.excludedCountries.length > 0 && (
                        <button
                          type="button"
                          className="sc-clear-link"
                          onClick={() => updateSf('excludedCountries', [])}
                          disabled={starting || isSearching}
                        >
                          Clear selection
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Section 3 — Industry & filters */}
                  <div className="sc-section">
                    <div className="sc-section-label">Industry &amp; matching</div>

                    <div className="sc-field-row">
                      <span className="sc-field-desc">
                        Which industries should be targeted?{' '}
                        <span className="sc-optional">(optional)</span>
                      </span>
                      <div className="sc-chip-group">
                        {INDUSTRY_OPTIONS.map((ind) => (
                          <button
                            key={ind}
                            type="button"
                            className={`sc-chip ${sf.industries.includes(ind) ? 'sc-chip--active' : ''}`}
                            onClick={() => toggleSfChip('industries', ind)}
                            disabled={starting || isSearching}
                          >
                            {ind}
                          </button>
                        ))}
                        <button
                          type="button"
                          className={`sc-chip ${sf.industries.includes('Other') ? 'sc-chip--active' : ''}`}
                          onClick={() => toggleSfChip('industries', 'Other')}
                          disabled={starting || isSearching}
                        >
                          Other
                        </button>
                      </div>
                      {sf.industries.includes('Other') && (
                        <input
                          className="sc-text-input sc-text-input--inline"
                          type="text"
                          placeholder="Enter industry name…"
                          value={sf.customIndustry}
                          onChange={(e) => updateSf('customIndustry', e.target.value)}
                          disabled={starting || isSearching}
                          autoFocus
                        />
                      )}
                    </div>

                  </div>

                  {/* Section 4 — Search settings */}
                  <div className="sc-section">
                    <div className="sc-section-label">Search settings</div>

                    <div className="sc-field-row">
                      <span className="sc-field-desc">What search mode should be used?</span>
                      <div className="sc-toggle-group">
                        <button
                          type="button"
                          className={`sc-toggle-btn ${sf.searchType === 'pro' ? 'sc-toggle-btn--active' : ''}`}
                          onClick={() => updateSf('searchType', 'pro')}
                          disabled={starting || isSearching}
                        >
                          <span className="sc-toggle-label">Pro</span>
                          <span className="sc-toggle-hint">30–180s · 5 credits</span>
                        </button>
                        <button
                          type="button"
                          className={`sc-toggle-btn ${sf.searchType === 'fast' ? 'sc-toggle-btn--active' : ''}`}
                          onClick={() => updateSf('searchType', 'fast')}
                          disabled={starting || isSearching}
                        >
                          <span className="sc-toggle-label">Fast</span>
                          <span className="sc-toggle-hint">under 30s · 1 credit</span>
                        </button>
                      </div>
                    </div>

                    <div className="sc-field-row sc-field-row--stack">
                      <span className="sc-field-desc">
                        How many candidates do you need?{' '}
                        <strong className="sc-slider-val">{sf.limit} results</strong>
                      </span>
                      <div className="sc-slider-wrap">
                        <input
                          type="range"
                          className="sc-slider"
                          min={5}
                          max={20}
                          step={1}
                          value={sf.limit}
                          onChange={(e) => updateSf('limit', Number(e.target.value))}
                          disabled={starting || isSearching}
                        />
                        <div className="sc-slider-labels">
                          <span>5 results</span>
                          <span>20 results</span>
                        </div>
                      </div>
                    </div>

                    <div className="sc-field-row">
                      <span className="sc-field-desc">
                        Should the AI provide insights explaining each match?
                      </span>
                      <div className="sc-toggle-group">
                        <button
                          type="button"
                          className={`sc-toggle-btn ${sf.insights ? 'sc-toggle-btn--active' : ''}`}
                          onClick={() => updateSf('insights', true)}
                          disabled={starting || isSearching}
                        >
                          <span className="sc-toggle-label">Yes</span>
                          <span className="sc-toggle-hint">+1 credit each</span>
                        </button>
                        <button
                          type="button"
                          className={`sc-toggle-btn ${!sf.insights ? 'sc-toggle-btn--active' : ''}`}
                          onClick={() => updateSf('insights', false)}
                          disabled={starting || isSearching}
                        >
                          <span className="sc-toggle-label">No</span>
                          <span className="sc-toggle-hint">results only</span>
                        </button>
                      </div>
                    </div>

                    <div className="sc-field-row">
                      <span className="sc-field-desc">
                        Should fresh real-time profile data be fetched?
                      </span>
                      <div className="sc-toggle-group">
                        <button
                          type="button"
                          className={`sc-toggle-btn ${!sf.highFreshness ? 'sc-toggle-btn--active' : ''}`}
                          onClick={() => updateSf('highFreshness', false)}
                          disabled={starting || isSearching}
                        >
                          <span className="sc-toggle-label">Standard</span>
                          <span className="sc-toggle-hint">cached data</span>
                        </button>
                        <button
                          type="button"
                          className={`sc-toggle-btn ${sf.highFreshness ? 'sc-toggle-btn--active' : ''}`}
                          onClick={() => updateSf('highFreshness', true)}
                          disabled={starting || isSearching}
                        >
                          <span className="sc-toggle-label">Live scrape</span>
                          <span className="sc-toggle-hint">+2 credits · ~30s extra</span>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Section 5 — Contact data */}
                  <div className="sc-section sc-section--last">
                    <div className="sc-section-label">Contact data</div>

                    {/* Single-select: contact filter requirement */}
                    <div className="sc-field-row">
                      <span className="sc-field-desc">
                        Which contact details are required?{' '}
                        <span className="sc-optional">(pick one)</span>
                      </span>
                      <div className="sc-chip-group">
                        {FILTER_OPTIONS.map((opt) => (
                          <button
                            key={opt.id}
                            type="button"
                            className={`sc-chip ${sf.filterOption === opt.id ? 'sc-chip--active' : ''}`}
                            onClick={() => updateSf('filterOption', opt.id)}
                            disabled={starting || isSearching}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Multi-select: reveal actual contact details */}
                    <div className="sc-field-row">
                      <span className="sc-field-desc">
                        Reveal actual contact details?{' '}
                        <span className="sc-optional">(select all that apply)</span>
                      </span>
                      <div className="sc-chip-group">
                        {[
                          { id: 'reveal_emails', label: 'Reveal emails (+6 cr each)' },
                          { id: 'reveal_phones', label: 'Reveal phones (+6 cr each)' },
                        ].map((opt) => (
                          <button
                            key={opt.id}
                            type="button"
                            className={`sc-chip ${sf.revealOptions.includes(opt.id) ? 'sc-chip--active' : ''}`}
                            onClick={() => toggleSfChip('revealOptions', opt.id)}
                            disabled={starting || isSearching}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Credit estimator + submit row */}
                <div className="sc-structured-footer">
                  <div className="sc-credit-estimate">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                      <path
                        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
                        fill="#8b5cf6"
                      />
                    </svg>
                    Estimated cost:{' '}
                    <strong className="sc-credit-total">{estimatedCredits} credits</strong>
                    <span className="sc-credit-detail">
                      ({sf.limit} candidates × {perCandidateCost} cr each)
                    </span>
                  </div>
                  <button className="sc-submit-btn" type="submit" disabled={isSubmitDisabled}>
                    {starting || isSearching ? (
                      <>
                        <span className="sc-spinner" />
                        Searching...
                      </>
                    ) : (
                      <>
                        <SearchIcon />
                        Run search
                      </>
                    )}
                  </button>
                </div>
              </>
            )}

            {/* ── Clarification / Enhancement step ── */}
            {clarifyState && (
              <div className="sc-clarify-wrap">

                {/* State 1: Fetching questions */}
                {clarifyState === 'clarifying' && (
                  <div className="sc-clarify-status">
                    <span className="sc-spinner" />
                    <span>Analyzing your query…</span>
                  </div>
                )}

                {/* State 2: Questions ready */}
                {clarifyState !== 'clarifying' && clarifyState !== 'enhancing' && !clarifyState.enhanced_query && (
                  <div className="sc-clarify-panel">
                    <div className="sc-clarify-panel-header">
                      <span className="sc-clarify-badge">Help us prioritize</span>
                      <button type="button" className="sc-clarify-skip" onClick={handleSkipClarify}>
                        Skip → search with original query
                      </button>
                    </div>

                    {clarifyState.questions?.map((q) => (
                      <div key={q.id} className="sc-clarify-question">
                        <p className="sc-clarify-q-text">{q.text}</p>
                        <div className="sc-chip-group">
                          {q.options.map((opt) => {
                            const active = clarifyAnswers[q.id]?.id === opt.id;
                            return (
                              <button
                                key={opt.id}
                                type="button"
                                className={`sc-chip${active ? ' sc-chip--active' : ''}`}
                                onClick={() =>
                                  setClarifyAnswers((prev) => ({
                                    ...prev,
                                    [q.id]: {
                                      id: opt.id,
                                      label: opt.label,
                                      hint: opt.hint,
                                      question_text: q.text,
                                    },
                                  }))
                                }
                              >
                                {opt.label}
                                {opt.hint && (
                                  <span className="sc-chip-hint"> · {opt.hint}</span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}

                    <button
                      type="button"
                      className="sc-submit-btn sc-clarify-confirm-btn"
                      onClick={handleConfirmAnswers}
                      disabled={starting}
                    >
                      Confirm Priorities →
                    </button>
                  </div>
                )}

                {/* State 3: Enhancing query */}
                {clarifyState === 'enhancing' && (
                  <div className="sc-clarify-status">
                    <span className="sc-spinner" />
                    <span>Enriching your query…</span>
                  </div>
                )}

                {/* State 4: Enhanced query preview */}
                {clarifyState?.enhanced_query && (
                  <div className="sc-clarify-panel sc-clarify-panel--preview">
                    <div className="sc-clarify-panel-header">
                      <span className="sc-clarify-badge sc-clarify-badge--done">✨ Enhanced query</span>
                      <button type="button" className="sc-clarify-skip" onClick={handleSkipClarify}>
                        Skip → use original instead
                      </button>
                    </div>
                    <p className="sc-clarify-enhanced-text">"{clarifyState.enhanced_query}"</p>
                    <button
                      type="button"
                      className="sc-submit-btn"
                      onClick={handleRunSearch}
                      disabled={starting || isSearching}
                    >
                      {starting || isSearching ? (
                        <><span className="sc-spinner" /> Starting…</>
                      ) : (
                        'Run Search'
                      )}
                    </button>
                  </div>
                )}

              </div>
            )}

            {searchError && (
              <div className="sc-error-banner">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
                    fill="currentColor"
                  />
                </svg>
                {searchError}
              </div>
            )}
          </form>
        </div>

        <aside className="sc-col-recent" aria-label="Recent searches">
          <div className="sc-recent-header">
            <h2 className="sc-recent-title">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42C8.27 19.99 10.51 21 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z" fill="currentColor"/>
              </svg>
              Search History
            </h2>
            {recentSessions.length > 0 && (
              <span className="sc-recent-count">{recentSessions.length}</span>
            )}
          </div>

          {recentLoading ? (
            <div className="sc-recent-loading">
              <span className="sc-recent-spinner" />
              Loading…
            </div>
          ) : recentSessions.length === 0 ? (
            <div className="sc-recent-empty">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" fill="#c4b5fd"/>
              </svg>
              <p>No searches yet.<br />Run a search to build your history.</p>
            </div>
          ) : (
            <>
              <ul className="sc-recent-list">
                {pagedSessions.map((session, idx) => (
                  <li key={session.id}>
                    <div className="sc-recent-item">
                      <div className="sc-recent-item-top">
                        <span className="sc-recent-index">
                          {(recentPage - 1) * RECENT_PAGE_SIZE + idx + 1}
                        </span>
                        <span className="sc-recent-query" title={session.query}>
                          {trimQuery(session.query, 80)}
                        </span>
                      </div>
                      <div className="sc-recent-item-meta">
                        <span className="sc-recent-date">
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden>
                            <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z" fill="currentColor"/>
                          </svg>
                          {formatSearchDate(session.created_at)}
                        </span>
                        {session.result_count != null && (
                          <span className="sc-recent-result-count">
                            {session.result_count} candidate{session.result_count !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      <div className="sc-recent-item-actions">
                        <button
                          type="button"
                          className="sc-recent-action sc-recent-action--view"
                          onClick={() =>
                            navigate(`/matchmaker?tab=custom&expand=${encodeURIComponent(session.id)}`)
                          }
                        >
                          View results
                        </button>
                        <button
                          type="button"
                          className="sc-recent-action sc-recent-action--repeat"
                          onClick={() => handleRepeatSearch(session.query)}
                          disabled={starting || isSearching}
                          title="Repeat this search"
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
                            <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" fill="currentColor"/>
                          </svg>
                          Repeat
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>

              {recentTotalPages > 1 && (
                <div className="sc-recent-pagination">
                  <button
                    type="button"
                    className="sc-page-btn"
                    onClick={() => setRecentPage((p) => Math.max(1, p - 1))}
                    disabled={recentPage === 1}
                    aria-label="Previous page"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                      <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" fill="currentColor"/>
                    </svg>
                  </button>
                  <span className="sc-page-info">
                    {recentPage} / {recentTotalPages}
                  </span>
                  <button
                    type="button"
                    className="sc-page-btn"
                    onClick={() => setRecentPage((p) => Math.min(recentTotalPages, p + 1))}
                    disabled={recentPage === recentTotalPages}
                    aria-label="Next page"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                      <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" fill="currentColor"/>
                    </svg>
                  </button>
                </div>
              )}
            </>
          )}
        </aside>
      </div>

      {results !== null && (
        <div className="sc-results">
          <div className="sc-results-header">
            <div className="sc-results-header-left">
              <div className="sc-results-title-row">
                <h3>
                  {results.length === 0 ? 'No candidates found' : `${results.length} candidate${results.length !== 1 ? 's' : ''} found`}
                </h3>
                <span className="sc-source-badge sc-source-badge--custom">Custom Search</span>
              </div>
              {lastSearchPayload?.query && (
                <p className="sc-results-query-preview">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" fill="currentColor"/>
                  </svg>
                  {trimQuery(lastSearchPayload.query, 100)}
                </p>
              )}
            </div>
            <div className="sc-results-header-actions">
              {lastSearchPayload && (
                <button
                  className="sc-new-batch-btn"
                  onClick={handleNewBatch}
                  disabled={starting || isSearching}
                  title="Run a new search with the same criteria, excluding all candidates seen so far"
                >
                  {starting || isSearching ? (
                    <>
                      <span className="sc-spinner sc-spinner--purple" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                        <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" fill="currentColor" />
                      </svg>
                      New Batch
                    </>
                  )}
                </button>
              )}
              {sessionId && (
                <button
                  className="sc-view-matchmaker-btn"
                  onClick={() =>
                    navigate(`/matchmaker?tab=custom&expand=${encodeURIComponent(sessionId)}`)
                  }
                >
                  View in Matchmaker
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" fill="currentColor" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {results.length === 0 ? (
            <div className="sc-empty">
              {lastSearchPayload?.searchParams?.filter_out_no_emails ||
               lastSearchPayload?.searchParams?.filter_out_no_phones ||
               lastSearchPayload?.searchParams?.filter_out_no_phones_or_emails ? (
                <>
                  <p>No candidates found with the required contact data.</p>
                  <p className="sc-empty-hint">
                    The contact filter (email/phone requirement) significantly reduces the candidate pool.
                    Try removing the contact requirement and enabling "Reveal emails" separately to see
                    more candidates.
                  </p>
                </>
              ) : (
                <p>Try broadening your query or adjusting the criteria.</p>
              )}
            </div>
          ) : (
            <div className="sc-matches-grid">
              {results.map((match) => (
                <MatchCard
                  key={match.id}
                  match={match}
                  getMatchPercentageClass={getMatchPercentageClass}
                  onViewDetails={handleViewDetails}
                  onThumbsUp={() => handleCustomFeedback(match.id, 'thumbs_up')}
                  onThumbsDown={() => openThumbsDown(match.id)}
                  onResetFeedback={() => handleCustomReset(match.id)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {showModal && selectedMatch && (
        <CandidateProfileModal
          match={selectedMatch}
          candidate={selectedMatch.candidate}
          job={null}
          onClose={() => {
            setShowModal(false);
            setSelectedMatch(null);
          }}
        />
      )}

      {thumbsDownModal.open && (
        <div
          className="feedback-modal-overlay"
          onClick={() => setThumbsDownModal({ open: false, matchId: null })}
          role="presentation"
        >
          <div
            className="feedback-modal"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <div className="feedback-modal-header">
              <div className="feedback-modal-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path
                    d="M15 3H6c-.83 0-1.54.5-1.84 1.22l-3.02 7.05c-.09.23-.14.47-.14.73v2c0 1.1.9 2 2 2h6.31l-.95 4.57-.03.32c0 .41.17.79.44 1.06L9.83 23l6.59-6.59c.36-.36.58-.86.58-1.41V5c0-1.1-.9-2-2-2zm4 0v12h4V3h-4z"
                    fill="#ef4444"
                  />
                </svg>
              </div>
              <h3 className="feedback-modal-title">Why is this a poor match?</h3>
              <p className="feedback-modal-subtitle">
                Your feedback helps improve future candidate ranking.
              </p>
            </div>
            <textarea
              className="feedback-modal-textarea"
              placeholder="e.g. Missing required practice area, insufficient experience, location mismatch..."
              value={thumbsDownReason}
              onChange={(e) => setThumbsDownReason(e.target.value)}
              rows={4}
            />
            <div className="feedback-modal-actions">
              <button
                type="button"
                className="feedback-modal-btn feedback-modal-btn--skip"
                onClick={handleThumbsDownSkip}
              >
                Skip
              </button>
              <button
                type="button"
                className="feedback-modal-btn feedback-modal-btn--confirm"
                onClick={handleThumbsDownConfirm}
              >
                Submit Feedback
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MatchCard({
  match,
  getMatchPercentageClass,
  onViewDetails,
  onThumbsUp,
  onThumbsDown,
  onResetFeedback,
}) {
  return (
    <div className={`sc-match-card${match.feedback === 'thumbs_down' ? ' sc-match-card--rejected' : ''}`}>
      <div className="sc-match-card-header">
        <div className="sc-candidate-info">
          <div className="sc-candidate-photo-wrapper">
            <div className="sc-candidate-photo-placeholder">
              {match.candidate?.first_name?.[0] || '?'}
            </div>
            {match.candidate?.picture_url && (
              <img
                src={match.candidate.picture_url}
                alt={match.candidate.full_name}
                className="sc-candidate-photo"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            )}
          </div>
          <div className="sc-candidate-details">
            <h4 className="sc-candidate-name">{match.candidate?.full_name || 'Unknown'}</h4>
            {match.candidate?.title && (
              <p className="sc-candidate-role">{match.candidate.title}</p>
            )}
          </div>
        </div>

        <div className="sc-score-badges-column">
          <div className="sc-score-badges">
            <div className="sc-score-badge sc-score-badge--pearch">
              <span className="sc-score-value">{match.pearch_score * 20}</span>
              <span className="sc-score-label">Pearch</span>
            </div>
            <div className="sc-score-badge sc-score-badge--custom">
              <span className="sc-score-value">
                {match.custom_score != null ? Math.round(match.custom_score) : '—'}
              </span>
              <span className="sc-score-label">Custom</span>
            </div>
          </div>
          <div className="confidence-badge">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="#8b5cf6" />
            </svg>
            High Confidence
          </div>
        </div>
      </div>

      <div className="sc-candidate-secondary">
        <p className="sc-candidate-meta sc-candidate-meta--block">
          <span className="sc-candidate-exp">
            {match.candidate?.total_experience_years || 0} years experience
          </span>
          <span className="sc-sep" aria-hidden>
            {' '}
            ·{' '}
          </span>
          <span className="sc-candidate-location">
            {match.candidate?.location || 'Unknown location'}
          </span>
        </p>
        {match.created_at && (
          <div className="sc-candidate-matched">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path
                d="M12 2C6.5 2 2 6.5 2 12S6.5 22 12 22 22 17.5 22 12 17.5 2 12 2M12 20C7.59 20 4 16.41 4 12S7.59 4 12 4 20 7.59 20 12 16.41 20 12 20M12.5 7H11V13L16.2 16.2L17 14.9L12.5 12.2V7Z"
                fill="#8b5cf6"
              />
            </svg>
            <span className="sc-candidate-matched-text">
              {formatMatchDateLine(match.created_at)}
            </span>
          </div>
        )}
      </div>

      {match.candidate?.expertise?.length > 0 && (
        <div className="sc-practice-tags">
          {match.candidate.expertise.slice(0, 3).map((skill, idx) => (
            <span key={idx} className="sc-practice-tag">
              {skill}
            </span>
          ))}
        </div>
      )}

      <div className="sc-match-metrics">
        {[
          { label: 'Experience Match', value: match.experience_match },
          { label: 'Skills Alignment', value: match.skills_alignment },
          { label: 'Cultural Fit', value: match.cultural_fit },
        ].map(({ label, value }) => (
          <div key={label} className="sc-metric-item">
            <div className="sc-metric-header">
              <span className="sc-metric-label">{label}</span>
              <span className={`sc-metric-pct sc-metric-pct--${getMatchPercentageClass(value)}`}>
                {value}%
              </span>
            </div>
            <div className="sc-metric-bar">
              <div
                className={`sc-metric-fill sc-metric-fill--${getMatchPercentageClass(value)}`}
                style={{ width: `${value}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="match-feedback sc-match-feedback">
        <button
          type="button"
          className={`feedback-btn feedback-btn--up ${match.feedback === 'thumbs_up' ? 'active' : ''}`}
          onClick={onThumbsUp}
          disabled={!!match.feedback}
          title={match.feedback ? 'Feedback already submitted — reset to change' : 'Good match'}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z"
              fill="currentColor"
            />
          </svg>
          Good match
        </button>
        <button
          type="button"
          className={`feedback-btn feedback-btn--down ${match.feedback === 'thumbs_down' ? 'active' : ''}`}
          onClick={onThumbsDown}
          disabled={!!match.feedback}
          title={match.feedback ? 'Feedback already submitted — reset to change' : 'Poor match'}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M15 3H6c-.83 0-1.54.5-1.84 1.22l-3.02 7.05c-.09.23-.14.47-.14.73v2c0 1.1.9 2 2 2h6.31l-.95 4.57-.03.32c0 .41.17.79.44 1.06L9.83 23l6.59-6.59c.36-.36.58-.86.58-1.41V5c0-1.1-.9-2-2-2zm4 0v12h4V3h-4z"
              fill="currentColor"
            />
          </svg>
          Poor match
        </button>
        {match.feedback && (
          <button
            type="button"
            className="feedback-btn feedback-btn--reset"
            onClick={onResetFeedback}
            title="Reset feedback"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path
                d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"
                fill="currentColor"
              />
            </svg>
            Reset
          </button>
        )}
      </div>

      <button className="sc-btn-view-details" onClick={() => onViewDetails(match)}>
        View Details
      </button>
    </div>
  );
}

export default SearchCandidates;
